import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { ALL_CHANNELS, Channel, Measure, Periods } from 'cio/utils/metrics/common';
import { Grouping } from 'cio/utils/metrics/resolution';
import { SortDirection } from 'cio/utils/sort';
import { MEASURES } from 'cio/utils/metrics/analysis';
import { JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { ActionType } from 'cio/models/action';
import { actionTypeName } from 'cio/utils/action-info';
import AnalyticsService from 'cio/services/analytics';
import moment from 'moment-timezone';
import { EditType } from 'cio/utils/analytics/transforms/campaign';
import { LAST_30_DAYS_RANGE_PRESET } from '@customerio/fly-components/utils/fly-date-picker-utils';
import CampaignModel, { RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import { isEmpty } from '@ember/utils';
import SettingsService from 'cio/services/settings';
import { schedule } from '@ember/runloop';

const DELIVERY_METRICS_MEASURES = MEASURES.filter((m) => m.includes('delivery_metrics')).map((m) =>
  m.replace('delivery_metrics.', ''),
);

const DEFAULT_DELIVERY_METRICS_MEASURES: Measure[] = ['sent', 'opened', 'clicked'];
const JOURNEY_METRICS_MEASURES: JourneyMetricsMeasure[] = [
  'activated',
  'messaged',
  'in_workflow',
  'finished',
  'never_activated',
  'exited_early',
  'converted',
];
const DEFAULT_METRICS_TABLE_COLUMNS: Measure[] = ['sent', 'delivered', 'opened', 'clicked', 'unsubscribed'];

export default class CampaignOverviewMetricsIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service settings!: SettingsService;

  declare model: CampaignModel;

  queryParams = [
    {
      startTime: { as: 'start_time' },
      endTime: { as: 'end_time' },
      period: { as: 'period' },
      grouping: { as: 'grouping' },
      unit: { as: 'unit' },
      performanceDeliveryChartType: { as: 'pd_chart_type' },
      performanceDeliveryChannel: { as: 'pd_channel' },
      performanceDeliveryIndividualMeasure: { as: 'pd_individual_measure' },
      performanceDeliveryMeasuresParam: { as: 'pd_measures' },
      journeysMeasuresParam: { as: 'journeys_measures' },
      messageMetricsChannelsParam: { as: 'message_channels' },
      messageMetricsSortBy: { as: 'message_sort_by' },
      messageMetricsSortDirection: { as: 'message_sort_direction' },
      messageMetricsColumnsParam: { as: 'message_columns' },
      graphTab: { as: 'tab' },
    },
  ];

  @tracked startTime = '';
  @tracked endTime = '';

  get timeRange() {
    if (this.startTime === '' || this.endTime === '') {
      return LAST_30_DAYS_RANGE_PRESET.getRange();
    }

    return { start: moment(this.startTime), end: moment(this.endTime) };
  }

  @tracked period = Periods.DAYS;

  @tracked grouping = Grouping.Daily;

  @tracked unit: 'count' | 'percentage' = 'percentage';

  @tracked graphTab: 'delivery' | 'journey' = 'delivery';

  @tracked performanceDeliveryChartType: 'individual' | 'aggregate' = 'aggregate';
  @tracked performanceDeliveryChannel: Channel = 'email';
  @tracked performanceDeliveryIndividualMeasure: Measure = 'sent';
  @tracked performanceDeliveryMeasures: Measure[] = [...DEFAULT_DELIVERY_METRICS_MEASURES];
  @tracked performanceDeliveryMeasuresParam: string = DEFAULT_DELIVERY_METRICS_MEASURES.join('-');

  @tracked journeysMeasures: JourneyMetricsMeasure[] = [...JOURNEY_METRICS_MEASURES];
  @tracked journeysMeasuresParam: string = JOURNEY_METRICS_MEASURES.join('-');

  @tracked messageMetricsChannels: Channel[] = [];
  @tracked messageMetricsChannelsParam = this.messageMetricsChannels.join('-');
  @tracked messageMetricsSortBy: 'action_name' | Measure = 'action_name';
  @tracked messageMetricsSortDirection: SortDirection = SortDirection.Ascending;
  @tracked messageMetricsColumns: Measure[] = [...DEFAULT_METRICS_TABLE_COLUMNS];
  @tracked messageMetricsColumnsParam: string = DEFAULT_METRICS_TABLE_COLUMNS.join('-');

  @action
  onTimeRangeChange(startTime: string, endTime: string) {
    this.startTime = startTime;
    this.endTime = endTime;

    this.analytics.track('campaign', 'metrics_timeframe_filter_changed', {
      model: this.model,
      timePeriod: this.period,
      timeRange: this.timeRange,
      grouping: this.grouping,
      chartUnit: this.unit,
      editType: EditType.TimeRange,
    });
  }

  @action
  onPeriodChange(period: Periods) {
    this.period = period;
  }

  @action
  onGroupingChange(grouping: Grouping, trackEvent = true) {
    this.grouping = grouping;

    if (trackEvent) {
      this.analytics.track('campaign', 'metrics_timeframe_filter_changed', {
        model: this.model,
        timePeriod: this.period,
        timeRange: this.timeRange,
        grouping: this.grouping,
        chartUnit: this.unit,
        editType: EditType.Grouping,
      });
    }
  }

  @action
  onUnitChange(unit: 'count' | 'percentage') {
    this.unit = unit;

    // 'started' only valid with unit set to count, so ensure it's not enabled
    if (unit === 'percentage' && this.journeysMeasures.includes('started')) {
      this.onJourneysMeasuresChange(
        this.journeysMeasures.filter((m) => m !== 'started'),
        false,
      );
    }

    this.analytics.track('campaign', 'metrics_timeframe_filter_changed', {
      model: this.model,
      timePeriod: this.period,
      timeRange: this.timeRange,
      grouping: this.grouping,
      chartUnit: this.unit,
      editType: EditType.ChartUnit,
    });
  }

  @action
  onGraphTabChange(tab: 'delivery' | 'journey') {
    this.graphTab = tab;
  }

  @action
  onPerformanceDeliveryChartTypeChange(type: 'individual' | 'aggregate') {
    this.performanceDeliveryChartType = type;
  }

  @action
  onPerformanceDeliveryChannelChange(channel: Channel) {
    this.performanceDeliveryChannel = channel;
  }

  @action
  onPerformanceDeliveryIndividualMeasureChange(measure: Measure) {
    this.performanceDeliveryIndividualMeasure = measure;
  }

  @action
  onPerformanceDeliveryMeasuresChange(measures: Measure[]) {
    // We set measures like this to maintain the default sort order
    this.performanceDeliveryMeasures = measures.sort(
      (a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b),
    );

    this.serializeQueryParams();
  }

  @action
  onJourneysMeasuresChange(measures: JourneyMetricsMeasure[], trackEvent = true) {
    this.journeysMeasures = measures.sort(
      (a, b) => JOURNEY_METRICS_MEASURES.indexOf(a) - JOURNEY_METRICS_MEASURES.indexOf(b),
    );

    this.serializeQueryParams();

    if (trackEvent) {
      this.analytics.track('campaign', 'journey_metrics_chart_changed', {
        model: this.model,
        selectedMetrics: this.journeysMeasures,
      });
    }
  }

  @action
  onMessageMetricsChannelsChange(channels: Channel[]) {
    // We set channels like this rather than directly to maintain the default
    // sort order
    this.messageMetricsChannels = [...channels].sort((a, b) => ALL_CHANNELS.indexOf(a) - ALL_CHANNELS.indexOf(b));

    this.serializeQueryParams();

    this.analytics.track('campaign', 'message_metrics_table_changed', {
      model: this.model,
      channels: this.messageMetricsChannels,
      selectedMetrics: this.messageMetricsColumns,
      sortBy: this.messageMetricsSortBy,
      sortDirection: this.messageMetricsSortDirection,
      editType: EditType.Channels,
    });
  }

  @action
  onMessageMetricsSortChange(sortBy: 'action_name' | Measure, sortDirection: SortDirection) {
    let oldSortBy = this.messageMetricsSortBy;
    let oldSortDirection = this.messageMetricsSortDirection;

    this.messageMetricsSortBy = sortBy;
    this.messageMetricsSortDirection = sortDirection;

    let editType = null;

    if (sortBy !== oldSortBy) {
      editType = EditType.SortBy;
    } else if (sortDirection !== oldSortDirection) {
      editType = EditType.SortDirection;
    }

    if (editType != null) {
      this.analytics.track('campaign', 'message_metrics_table_changed', {
        model: this.model,
        channels: this.messageMetricsChannels,
        selectedMetrics: this.messageMetricsColumns,
        sortBy: this.messageMetricsSortBy,
        sortDirection: this.messageMetricsSortDirection,
        editType,
      });
    }
  }

  @action
  onMessageMetricsColumnsChange(measures: Measure[], track = true) {
    this.messageMetricsColumns = measures.sort(
      (a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b),
    );

    this.serializeQueryParams();

    if (track) {
      this.analytics.track('campaign', 'message_metrics_table_changed', {
        model: this.model,
        channels: this.messageMetricsChannels,
        selectedMetrics: this.messageMetricsColumns,
        sortBy: this.messageMetricsSortBy,
        sortDirection: this.messageMetricsSortDirection,
        editType: EditType.SelectedMetrics,
      });
    }
  }

  @action
  deserializeQueryParams() {
    let pdMeasures = (
      this.performanceDeliveryMeasuresParam === '' ? [] : this.performanceDeliveryMeasuresParam.split('-')
    ) as Measure[];
    this.performanceDeliveryMeasures = pdMeasures.sort(
      (a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b),
    );

    let journeysMeasures = (
      this.journeysMeasuresParam === '' ? [] : this.journeysMeasuresParam.split('-')
    ) as JourneyMetricsMeasure[];
    this.journeysMeasures = journeysMeasures.sort(
      (a, b) => JOURNEY_METRICS_MEASURES.indexOf(a) - JOURNEY_METRICS_MEASURES.indexOf(b),
    ) as JourneyMetricsMeasure[];

    let messageMetricsChannels = (
      this.messageMetricsChannelsParam === '' ? [] : this.messageMetricsChannelsParam.split('-')
    ) as Channel[];
    this.messageMetricsChannels = messageMetricsChannels.sort(
      (a, b) => ALL_CHANNELS.indexOf(a) - ALL_CHANNELS.indexOf(b),
    );

    let messageMetricsColumns = (
      this.messageMetricsColumnsParam === '' ? [] : this.messageMetricsColumnsParam.split('-')
    ) as Measure[];
    this.messageMetricsColumns = messageMetricsColumns.sort(
      (a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b),
    );
  }

  @action
  serializeQueryParams() {
    this.performanceDeliveryMeasuresParam = this.performanceDeliveryMeasures.join('-');

    this.journeysMeasuresParam = this.journeysMeasures.join('-');

    this.messageMetricsChannelsParam = this.messageMetricsChannels.join('-');

    this.messageMetricsColumnsParam = this.messageMetricsColumns.join('-');
  }

  @action
  resetQueryParams() {
    this.startTime = '';
    this.endTime = '';
    this.period = Periods.DAYS;
    this.grouping = Grouping.Daily;
    this.unit = 'percentage';
    this.graphTab = 'delivery';
    this.journeysMeasures = [...JOURNEY_METRICS_MEASURES];
    this.messageMetricsChannels = this.getAllAvailableChannels();
    this.messageMetricsSortBy = 'action_name';
    this.messageMetricsSortDirection = SortDirection.Ascending;
    this.messageMetricsColumns = [...DEFAULT_METRICS_TABLE_COLUMNS];

    this.performanceDeliveryChartType = 'aggregate';
    this.performanceDeliveryChannel = this.messageMetricsChannels[0] ?? 'email';
    this.performanceDeliveryIndividualMeasure = 'sent';
    this.performanceDeliveryMeasures = [...DEFAULT_DELIVERY_METRICS_MEASURES];

    this.serializeQueryParams();
  }

  @action
  getAllAvailableChannels() {
    return [...ALL_CHANNELS]
      .map((channel) => `${channel}_action`)
      .filter((type: ActionType) => {
        let restrictedTypes = RESTRICTED_ACTION_TYPES[this.model.type] ?? [];
        return !restrictedTypes.includes(type) && this.environment.enabledAction(type);
      })
      .map((type: ActionType) => actionTypeName(type)) as Channel[];
  }

  @action
  setAllAvailableChannels() {
    if (this.messageMetricsChannels.length === 0) {
      let availableChannels = this.getAllAvailableChannels();
      this.messageMetricsChannels = [...availableChannels];
      this.messageMetricsChannelsParam = availableChannels.join('_');

      if (!availableChannels.includes(this.performanceDeliveryChannel)) {
        this.performanceDeliveryChannel = availableChannels[0] ?? 'email';
      }
    }
  }

  // If the endTime query param is a future date, reset it to today (we can't
  // retrieve metrics from the future).
  @action
  limitEndTime() {
    if (isEmpty(this.endTime)) {
      return;
    }

    let endMoment = moment.tz(this.endTime, this.settings.environmentSettings.metricsTimezone).startOf('day');
    let maxMoment = moment.tz(this.settings.environmentSettings.metricsTimezone).startOf('day');

    if (endMoment.isAfter(maxMoment)) {
      // Run this in the routerTransitions hook to make sure the query param in
      // the URL gets updated.
      schedule('routerTransitions', () => {
        this.endTime = maxMoment.format('YYYY-MM-DD');
      });
    }
  }
}
